import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Eye, Shield, Users, Loader2, Save, Clock, User, 
  CheckCircle, XCircle, Info, History
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Available staff roles that can be granted impersonation privilege
const AVAILABLE_ROLES = [
  { id: 'admin', name: 'Administrator', description: 'Full clinic access (always enabled)' },
  { id: 'manager', name: 'Manager', description: 'Manage daily operations' },
  { id: 'receptionist', name: 'Receptionist', description: 'Front desk operations' },
  { id: 'nurse', name: 'Nurse', description: 'Clinical support staff' },
  { id: 'provider', name: 'Healthcare Provider', description: 'Doctors and providers' },
  { id: 'staff', name: 'General Staff', description: 'Basic staff member' }
];

const ImpersonationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    allowed_roles: ['admin']
  });
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchSessions();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/clinic/impersonation-privileges`);
      setSettings({
        enabled: response.data.enabled ?? true,
        allowed_roles: response.data.allowed_roles || ['admin']
      });
    } catch (error) {
      console.error('Failed to fetch impersonation settings:', error);
      // Use defaults
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/clinic/patient-impersonation-sessions?limit=20`);
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  };

  const handleToggleRole = (roleId) => {
    // Admin is always allowed and cannot be toggled
    if (roleId === 'admin') return;

    const currentRoles = settings.allowed_roles || [];
    let newRoles;
    
    if (currentRoles.includes(roleId)) {
      newRoles = currentRoles.filter(r => r !== roleId);
    } else {
      newRoles = [...currentRoles, roleId];
    }
    
    // Ensure admin is always included
    if (!newRoles.includes('admin')) {
      newRoles.push('admin');
    }
    
    setSettings(prev => ({ ...prev, allowed_roles: newRoles }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/clinic/impersonation-privileges`, {
        enabled: settings.enabled,
        allowed_roles: settings.allowed_roles
      });
      toast.success('Impersonation settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Settings Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Eye className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <CardTitle>Patient View Impersonation</CardTitle>
              <CardDescription>
                Configure which staff roles can view the patient portal as patients to assist them
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Info Alert */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>What is Patient View?</strong> This feature allows authorized staff to view the patient portal 
              exactly as a specific patient sees it. It's useful for helping patients navigate their portal, 
              troubleshooting issues, or filling out forms on their behalf. All sessions are logged for security.
            </AlertDescription>
          </Alert>

          {/* Feature Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-gray-500" />
              <div>
                <Label className="text-base font-medium">Enable Patient View Feature</Label>
                <p className="text-sm text-gray-500">When disabled, no one can view as patient</p>
              </div>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
            />
          </div>

          {/* Role Permissions */}
          {settings.enabled && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-gray-500" />
                <Label className="text-base font-medium">Allowed Roles</Label>
              </div>
              <p className="text-sm text-gray-500 -mt-2">
                Select which staff roles can use the "View as Patient" feature
              </p>

              <div className="grid gap-3">
                {AVAILABLE_ROLES.map((role) => {
                  const isAllowed = settings.allowed_roles.includes(role.id);
                  const isAdmin = role.id === 'admin';
                  
                  return (
                    <div 
                      key={role.id}
                      className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                        isAllowed ? 'border-teal-200 bg-teal-50 dark:border-teal-900 dark:bg-teal-900/20' : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${isAllowed ? 'bg-teal-100 dark:bg-teal-800' : 'bg-gray-100 dark:bg-gray-800'}`}>
                          <User className={`h-4 w-4 ${isAllowed ? 'text-teal-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{role.name}</span>
                            {isAdmin && (
                              <Badge variant="outline" className="text-xs">Always Enabled</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{role.description}</p>
                        </div>
                      </div>
                      <Switch
                        checked={isAllowed}
                        onCheckedChange={() => handleToggleRole(role.id)}
                        disabled={isAdmin}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Session History Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <History className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <CardTitle>Session History</CardTitle>
                <CardDescription>
                  Audit log of patient view sessions
                </CardDescription>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
            >
              {showHistory ? 'Hide History' : 'Show History'}
            </Button>
          </div>
        </CardHeader>
        
        {showHistory && (
          <CardContent>
            {sessions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Eye className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No impersonation sessions recorded yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff Member</TableHead>
                    <TableHead>Patient Viewed</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{session.original_staff_name}</p>
                          <p className="text-xs text-gray-500">{session.original_staff_role}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{session.impersonated_user_name}</p>
                          <p className="text-xs text-gray-500">{session.impersonated_patient_id}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(session.started_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-gray-400" />
                          <span className="text-sm">{session.duration}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {session.is_active ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <XCircle className="h-3 w-3 mr-1" />
                            Ended
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default ImpersonationSettings;
